import { useState } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { FilterPane } from '../shared/FilterPane';
import { StatsCards } from './StatsCards';
import { OrdersSummary } from './OrdersSummary';
import { RevenueChart } from './RevenueChart';
import { CustomerMap } from './CustomerMap';
import { ManageCard } from './ManageCard';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { useAuth } from '../../contexts/AuthContext';
import { useDashboard } from '../../hooks/useDashboard';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Building2, MapPin, AlertCircle, RefreshCw } from 'lucide-react';
import { Badge } from '../ui/badge';
import { useOrders } from '../../hooks/useOrders';

type FilterPeriod = 'today' | 'week' | 'month' | 'year' | 'custom';

interface DashboardProps {
  onNavigate?: (page: string) => void;
  onOrderClick?: (orderId: string) => void;
  onCustomerClick?: (customerId: string) => void;
  onCategoryClick?: (categoryId: string) => void;
}

export function RestaurantDashboard({ onNavigate, onOrderClick, onCustomerClick, onCategoryClick }: DashboardProps) {
  // Default to closed on mobile, open on desktop
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768;
    }
    return true;
  });
  
  const { currentBranch, restaurantName } = useRestaurant();
  const { user } = useAuth();

  // Update document title dynamically
  usePageTitle('Dashboard');
  
  const { orders: ordersData, stats: ordersStats } = useOrders({ page: 1, limit: 50 });

  // Use the custom dashboard hook with tenantId
  const {
    stats,
    recentOrders,
    revenueData,
    loading,
    error,
    dateRange,
    refresh,
    updateDateRange,
  } = useDashboard(user?.tenantId || user?.restaurantId || '');

  const handleDateRangeChange = (startDate: Date, endDate: Date, period: FilterPeriod) => {
    console.log('📅 Filter changed:', period, { start: startDate, end: endDate });
    updateDateRange({ start: startDate, end: endDate });
  };

  // Format role for display
  const getRoleDisplay = () => {
    if (!user?.role) return '';
    return user.role.charAt(0).toUpperCase() + user.role.slice(1);
  };

  const BranchIndicator = () => {
    if (!currentBranch) return null;
    
    return (
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800">
          <Building2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
          <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
            {currentBranch.name}
          </span>
          {currentBranch.isMain && (
            <Badge variant="secondary" className="ml-1.5 text-xs h-5 px-1.5">
              Main
            </Badge>
          )}
        </div>
        
        {currentBranch.address && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
            <MapPin className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {currentBranch.address}
            </span>
          </div>
        )}

        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
          <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
            {currentBranch.code}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        activePage="dashboard"
        onNavigate={onNavigate}
        ordersCount={ordersStats?.total || ordersData.length}
      />
      
      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
        sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'
      }`}>
        <TopBar 
          onMenuClick={() => setSidebarOpen(!sidebarOpen)} 
          sidebarOpen={sidebarOpen} 
          onNavigate={onNavigate}
          onOrderClick={onOrderClick}
          onCustomerClick={onCustomerClick}
          onCategoryClick={onCategoryClick}
        />
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
                <p className="text-gray-400 dark:text-gray-500 mt-1">
                  Welcome {restaurantName} {getRoleDisplay()}!
                </p>
                
                {/* Branch Indicator */}
                <BranchIndicator />
              </div>
              <div className="flex items-center gap-3">
                {/* Refresh Button */}
                <button
                  onClick={refresh}
                  disabled={loading}
                  className="p-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Refresh dashboard"
                >
                  <RefreshCw className={`w-5 h-5 text-gray-600 dark:text-gray-400 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <FilterPane onDateRangeChange={handleDateRangeChange} />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                    Failed to load dashboard data
                  </h3>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                    {error}
                  </p>
                  <button
                    onClick={refresh}
                    className="mt-2 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                  >
                    Try again
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                <StatsCards 
                  indices={[0, 1]} 
                  stats={stats}
                  loading={loading}
                  onNavigate={onNavigate} 
                />
                <OrdersSummary 
                  orders={recentOrders}
                  loading={loading}
                  onNavigate={onNavigate}
                  onOrderClick={onOrderClick}
                />
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <StatsCards 
                  indices={[2, 3]} 
                  stats={stats}
                  loading={loading}
                  onNavigate={onNavigate} 
                />
                <RevenueChart 
                  data={revenueData}
                  loading={loading}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
              <div className="lg:col-span-2">
                <CustomerMap dateRange={dateRange} />
              </div>
              <ManageCard onNavigate={onNavigate} />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}